import { styled } from "@linaria/react"
import { AnimatePresence, motion } from "framer-motion"
import { observer } from "mobx-react-lite"
import { useEffect } from "react"


import { AgentIterationStyles } from "@/components/AgentIteration/AgentIterationStyles.ts"
import { Ellipsis, EllipsisContainer } from "@/components/Ellipsis/Ellipsis.ts"
import { MarkdownWrapper } from "@/components/Markdown/MarkdownWrapper.tsx"
import { ReasoningResult_Icon } from "@/components/ReasoningStep/ReasoningResult_Icon.tsx"
import { SlidingTextModel } from "@/components/SlidingText/SlidingTextModel.ts"
import { useModel } from "@/hooks/useModel.ts"
import { ellipsis } from "@/styles/mixins.ts"

import type { DMDecoratedText, UIToolCallObservation } from "@/lib/client/generated/TypeDefinitions.ts"
import type { Property } from "csstype"


export const SlidingText = observer((props: {
  value: DMDecoratedText | string
  offset: number
  icon?: UIToolCallObservation.Icon
  transitionDuration?: number
  minDisplayDuration?: number
  color?: Property.Color
  mode?: "sync" | "popLayout" | "wait"
}) => {
  const offset = props.offset
  const duration = props.transitionDuration || 0.5
  const minDisplayDuration = props.minDisplayDuration || 1000

  const model = useModel(() => new SlidingTextModel(props.value, minDisplayDuration), [minDisplayDuration])

  useEffect(() => {
    model.updateValue(props.value)
  }, [props.value, model])

  const text = model.text

  return (
    <AnimatePresence mode={props.mode || "popLayout"} initial={false}>
      <motion.div
        key={text}
        initial={{ y: offset }}
        animate={{ y: 0 }}
        exit={{ y: -offset }}
        transition={{ duration }}
      >
        <EllipsisContainer>
          <EllipsisDescendants color={props.color}>
            {props.icon && <ReasoningResult_Icon icon={props.icon} />}
            <MarkdownWrapper
              text={model.displayedValue.get()}
              className={AgentIterationStyles.secondLevelMarkdown}
              inlineOnly
            />
          </EllipsisDescendants>
        </EllipsisContainer>
      </motion.div>
    </AnimatePresence>
  )
})

const EllipsisDescendants = styled(Ellipsis)<{
  color?: Property.Color
}>`
  display: inline-flex;
  flex-direction: row;
  align-items: center;
  gap: 4px;

  color: ${props => props.color || "inherit"};

  & * {
    ${ellipsis};
  }
`